/**
 * PlanetAtmosphereScatterMaterial — a planet's atmosphere seen from orbit through the SAME physical atmosphere the
 * ground sky is made of (sky/post P3, 2026-09-27 — realism-additions §7.1): Hillaire's Rayleigh + Mie + ozone
 * (`AtmosphereConfig`, e.g. Atlas's ATLAS_ATMOSPHERE) integrated per FRAGMENT along the view chord, with the sun's
 * transmittance to every sample marched too, so the limb is blue over the day side, reddens and thins across the
 * terminator, and vanishes over the night side — by the physics, not a tint.
 *
 * WHY NOT O'NEIL here (PlanetAtmosphereMaterial stays for other bodies): O'Neil's GPU Gems 2 scattering fits one
 * `scale` polynomial for a scale depth of 0.25 of the column and 1/λ⁴ Rayleigh from a tint — it cannot carry the sky's
 * own aerosol (Atlas's 50 km-visibility Mie column), its ozone (the blue of the twilight limb) nor Cornette-Shanks.
 * WHY NO LUTs: the recipe was the ground sky's transmittance / multi-scatter LUTs; this marches instead — the sector
 * pauses the ground sky's bakes and the WebGL2 fallback has no LUT set, so a LUT-fed shell would need both kept alive
 * off-planet. The march IS the transmittance LUT's integral (6 steps to the top of the air per sample; the planet's
 * shadow cuts it). Multiple scattering (Ψ_ms) is omitted: from orbit the limb is single-scatter dominated.
 *
 * GEOMETRY. A BACK-faced sphere at `thickness` × the planet radius (O'Neil's `Cull Front`): a fragment is the far exit
 * of its view ray, the entry is the analytic near intersection (the stable perpendicular form, in units of the shell
 * radius — float32 at megametres), the ray ends at the shell exit or the ground; the planet's own depth hides every
 * fragment behind the disc, so only the LIMB draws (additive). THE SCALE: the shell's height maps onto the config's
 * column (top − bottom km) and the planet's radius onto Rv = column / (thickness − 1) km — a 2.5 % shell is a 4,000 km
 * world under Earth's air (a physically-scaled 1.6 % shell on a 78 km body is a 1.2 km line, gone past ~1,000 km).
 *
 * UNITS. `illuminance` is the sun's top-of-air illuminance in the caller's scene units (Atlas: π × the planet
 * surface's `uPlanetSun`, the radiance of a white Lambert face at normal incidence × π); the output is radiance in
 * the same units × `intensity`. `planetAtmosphereScatterCpu` is the oracle the tests pin.
 */

import * as THREE from 'three/webgpu';
import {
  Fn, uniform, float, vec3, vec4, positionLocal, cameraPosition, modelWorldMatrixInverse,
  normalize, dot, sqrt, max, min, exp, abs, pow, Loop, length, select,
} from 'three/tsl';
import type { AtmosphereConfig, Vec3 } from '../AtmosphereConfig.js';
import { computeDensities, rayleighPhase, cornetteShanksPhase } from '../AtmosphereConfig.js';

export interface PlanetAtmosphereScatterConfig {
  /** The physical atmosphere (km, km⁻¹) — the ground sky's own config. */
  atmosphere: AtmosphereConfig;
  /** Planet radius in the mesh's local units (default 1). */
  innerRadius?: number;
  /** Shell radius ÷ planet radius (default the config's top ÷ bottom — physical scale). */
  thickness?: number;
  /** Sun direction (world, unit, toward the sun). */
  sunDirection?: THREE.Vector3;
  /** The sun's top-of-air illuminance in scene units (a vec3 node or colour; default 1). */
  illuminance?: unknown;
  /** View-chord samples (default 16) and sun-march samples per view sample (default 6). */
  viewSamples?: number;
  sunSamples?: number;
  /** Output multiplier (default 1). */
  intensity?: number;
}

export interface PlanetAtmosphereScatterUniforms {
  sunDirection: { value: THREE.Vector3 };
  intensity: { value: number };
}

/** The virtual planet radius (km) a shell of `thickness` maps the config's column onto. */
export function scatterPlanetRadiusKm(cfg: AtmosphereConfig, thickness: number): number {
  return (cfg.topRadius - cfg.bottomRadius) / Math.max(1e-4, thickness - 1);
}

/** Optical depth (km⁻¹·km, per channel) from a point at radius `r` (km, virtual) toward `sun` to the top of the air;
 *  null when the planet shadows it. `mu` = cos(zenith → sun). */
function sunOpticalDepthCpu(cfg: AtmosphereConfig, rv: number, top: number, r: number, mu: number, steps: number): Vec3 | null {
  if (mu < 0 && r * r * (mu * mu - 1) + rv * rv >= 0) return null; // the ray to the sun meets the ground
  const tTop = -r * mu + Math.sqrt(Math.max(0, r * r * mu * mu - r * r + top * top));
  const dt = tTop / steps;
  const od = { x: 0, y: 0, z: 0 };
  for (let i = 0; i < steps; i++) {
    const t = (i + 0.5) * dt;
    const rs = Math.sqrt(r * r + 2 * r * mu * t + t * t);
    const d = computeDensities(cfg, rs - rv);
    od.x += (cfg.rayleighScattering.x * d.rayleigh + cfg.mieExtinction.x * d.mie + cfg.ozoneExtinction.x * d.ozone) * dt;
    od.y += (cfg.rayleighScattering.y * d.rayleigh + cfg.mieExtinction.y * d.mie + cfg.ozoneExtinction.y * d.ozone) * dt;
    od.z += (cfg.rayleighScattering.z * d.rayleigh + cfg.mieExtinction.z * d.mie + cfg.ozoneExtinction.z * d.ozone) * dt;
  }
  return od;
}

/**
 * CPU oracle: the radiance (per unit top-of-air illuminance, per channel) along a view ray through the shell.
 * `cam` / `dir` in planet-radius units (the planet at the origin, radius 1); `sun` unit.
 */
export function planetAtmosphereScatterCpu(
  cfg: AtmosphereConfig, cam: Vec3, dir: Vec3, sun: Vec3,
  thickness = cfg.topRadius / cfg.bottomRadius, viewSamples = 16, sunSamples = 6,
): Vec3 {
  const rv = scatterPlanetRadiusKm(cfg, thickness), top = rv * thickness;
  // in shell units (radius 1): the stable chord
  const cx = cam.x / thickness, cy = cam.y / thickness, cz = cam.z / thickness;
  const tca = -(cx * dir.x + cy * dir.y + cz * dir.z);
  const d2 = cx * cx + cy * cy + cz * cz - tca * tca;
  if (d2 >= 1) return { x: 0, y: 0, z: 0 };
  const thc = Math.sqrt(1 - d2);
  const near = Math.max(0, tca - thc);
  let far = tca + thc;
  const inner = 1 / thickness;
  if (d2 < inner * inner) { const tg = tca - Math.sqrt(inner * inner - d2); if (tg > near) far = Math.min(far, tg); }
  if (far <= near) return { x: 0, y: 0, z: 0 };
  const segKm = (far - near) * top, dt = segKm / viewSamples;
  const nu = dir.x * sun.x + dir.y * sun.y + dir.z * sun.z;
  const pR = rayleighPhase(nu), pM = cornetteShanksPhase(nu, cfg.miePhaseG);
  const tv = { x: 1, y: 1, z: 1 }, L = { x: 0, y: 0, z: 0 };
  for (let i = 0; i < viewSamples; i++) {
    const t = near * top + (i + 0.5) * dt;
    const px = cx * top + dir.x * t, py = cy * top + dir.y * t, pz = cz * top + dir.z * t;
    const r = Math.hypot(px, py, pz);
    const d = computeDensities(cfg, r - rv);
    const mu = (px * sun.x + py * sun.y + pz * sun.z) / r;
    const od = sunOpticalDepthCpu(cfg, rv, top, r, mu, sunSamples);
    const ext = {
      x: cfg.rayleighScattering.x * d.rayleigh + cfg.mieExtinction.x * d.mie + cfg.ozoneExtinction.x * d.ozone,
      y: cfg.rayleighScattering.y * d.rayleigh + cfg.mieExtinction.y * d.mie + cfg.ozoneExtinction.y * d.ozone,
      z: cfg.rayleighScattering.z * d.rayleigh + cfg.mieExtinction.z * d.mie + cfg.ozoneExtinction.z * d.ozone,
    };
    if (od) {
      L.x += tv.x * (cfg.rayleighScattering.x * d.rayleigh * pR + cfg.mieScattering.x * d.mie * pM) * Math.exp(-od.x) * dt;
      L.y += tv.y * (cfg.rayleighScattering.y * d.rayleigh * pR + cfg.mieScattering.y * d.mie * pM) * Math.exp(-od.y) * dt;
      L.z += tv.z * (cfg.rayleighScattering.z * d.rayleigh * pR + cfg.mieScattering.z * d.mie * pM) * Math.exp(-od.z) * dt;
    }
    tv.x *= Math.exp(-ext.x * dt); tv.y *= Math.exp(-ext.y * dt); tv.z *= Math.exp(-ext.z * dt);
  }
  return L;
}

export function createPlanetAtmosphereScatterMaterial(
  config: PlanetAtmosphereScatterConfig,
): { material: THREE.NodeMaterial; uniforms: PlanetAtmosphereScatterUniforms } {
  const cfg = config.atmosphere;
  const inner = config.innerRadius ?? 1;
  const thickness = config.thickness ?? cfg.topRadius / cfg.bottomRadius;
  const shell = inner * thickness;
  const rv = scatterPlanetRadiusKm(cfg, thickness), topKm = rv * thickness;
  const nView = Math.max(4, Math.min(32, config.viewSamples ?? 16));
  const nSun = Math.max(2, Math.min(12, config.sunSamples ?? 6));
  const uSun = uniform(config.sunDirection?.clone().normalize() ?? new THREE.Vector3(1, 0, 0));
  const uIntensity = uniform(config.intensity ?? 1);
  const illum = (config.illuminance as ReturnType<typeof vec3> | undefined) ?? vec3(1, 1, 1);
  const bR = vec3(cfg.rayleighScattering.x, cfg.rayleighScattering.y, cfg.rayleighScattering.z);
  const bMs = vec3(cfg.mieScattering.x, cfg.mieScattering.y, cfg.mieScattering.z);
  const bMe = vec3(cfg.mieExtinction.x, cfg.mieExtinction.y, cfg.mieExtinction.z);
  const bO = vec3(cfg.ozoneExtinction.x, cfg.ozoneExtinction.y, cfg.ozoneExtinction.z);
  const g = cfg.miePhaseG, g2 = g * g;
  // densities at an altitude (km): Rayleigh / Mie exponentials, the ozone tent (atmosphereFn's; the floor at −1 km)
  const dens = (hKm: ReturnType<typeof float>) => {
    const h = max(hKm, float(-1));
    return vec3(exp(h.div(cfg.rayleighScaleHeight).negate()), exp(h.div(cfg.mieScaleHeight).negate()),
      max(float(0), float(1).sub(abs(h.sub(cfg.ozoneCenterAltitude)).div(cfg.ozoneHalfWidth))));
  };
  const extinction = (d: ReturnType<typeof vec3>) => bR.mul(d.x).add(bMe.mul(d.y)).add(bO.mul(d.z));

  const material = new THREE.MeshBasicNodeMaterial();
  material.transparent = true;
  material.depthWrite = false;
  material.depthTest = true;
  material.side = THREE.BackSide; // the far exit; the planet's depth removes every ray that ends in the ground
  material.blending = THREE.AdditiveBlending;
  material.fog = false;

  material.colorNode = Fn(() => {
    // SHELL units (radius 1): the stable chord; km = shell units × topKm
    const camS = modelWorldMatrixInverse.mul(vec4(cameraPosition, 1)).xyz.div(shell);
    const sunL = normalize(modelWorldMatrixInverse.mul(vec4(uSun, 0)).xyz);
    const ray = positionLocal.div(shell).sub(camS);
    const dir = ray.div(length(ray));
    const tca = dot(camS, dir).negate();
    const d2 = dot(camS, camS).sub(tca.mul(tca));
    const thc = sqrt(max(float(0), float(1).sub(d2)));
    const near = max(float(0), tca.sub(thc));
    const innerS = 1 / thickness;
    const ground = tca.sub(sqrt(max(float(0), float(innerS * innerS).sub(d2))));
    const hitsGround = d2.lessThan(innerS * innerS).and(ground.greaterThan(near));
    const far = select(hitsGround, min(tca.add(thc), ground), tca.add(thc));
    const segKm = max(float(0), far.sub(near)).mul(topKm);
    const dt = segKm.div(nView);
    const nu = dot(dir, sunL);
    const pR = float(3 / (16 * Math.PI)).mul(float(1).add(nu.mul(nu)));
    const pM = float((3 / (8 * Math.PI)) * (1 - g2) / (2 + g2)).mul(float(1).add(nu.mul(nu)))
      .div(pow(max(float(1 + g2).sub(nu.mul(2 * g)), float(1e-5)), float(1.5)));
    const tv = vec3(1, 1, 1).toVar();
    const L = vec3(0, 0, 0).toVar();
    const t = near.mul(topKm).add(dt.mul(0.5)).toVar();
    const camKm = camS.mul(topKm);
    Loop(nView, () => {
      const p = camKm.add(dir.mul(t));
      const r = length(p);
      const d = dens(r.sub(rv));
      const mu = dot(p, sunL).div(r);
      // the sun's transmittance to this sample: the planet shadow, else a short march to the top of the air
      const shadowed = mu.lessThan(0).and(r.mul(r).mul(mu.mul(mu).sub(1)).add(rv * rv).greaterThanEqual(0));
      const tTop = r.mul(mu).negate().add(sqrt(max(float(0), r.mul(r).mul(mu.mul(mu)).sub(r.mul(r)).add(topKm * topKm))));
      const ds = tTop.div(nSun);
      const od = vec3(0, 0, 0).toVar();
      const ts = ds.mul(0.5).toVar(); // an accumulator, not the loop index (a nested Loop's index is `j`, not `i`)
      Loop(nSun, () => {
        const rs = sqrt(r.mul(r).add(r.mul(mu).mul(ts).mul(2)).add(ts.mul(ts)));
        od.addAssign(extinction(dens(rs.sub(rv))).mul(ds));
        ts.addAssign(ds);
      });
      const sunT = select(shadowed, vec3(0, 0, 0), exp(od.negate()));
      L.addAssign(tv.mul(bR.mul(d.x).mul(pR).add(bMs.mul(d.y).mul(pM))).mul(sunT).mul(dt));
      tv.mulAssign(exp(extinction(d).mul(dt).negate()));
      t.addAssign(dt);
    });
    return vec4(L.mul(illum).mul(uIntensity), 1);
  })();

  return {
    material: material as THREE.NodeMaterial,
    uniforms: { sunDirection: uSun as unknown as { value: THREE.Vector3 }, intensity: uIntensity as unknown as { value: number } },
  };
}
